import { db, type Session } from '../db';
import { updateChild, getChildById } from '../stores/children.svelte.ts';

function toPlain<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export async function saveSession(
  childId: string,
  session: Omit<Session, 'id' | 'childId' | 'createdAt' | 'updatedAt'>,
): Promise<Session> {
  const now = Date.now();
  const entry: Session = {
    id: crypto.randomUUID(),
    childId,
    ...toPlain(session),
    createdAt: now,
    updatedAt: now,
  };
  await db.sessions.add(entry);

  // Persist smart defaults on the child, not on app settings.
  if (session.type === 'nursing' && session.side) {
    await updateChild(childId, {
      lastBreastUsed: session.side === 'both' ? null : session.side,
    });
  }
  if (session.type === 'pumping' && session.volumeMl) {
    await updateChild(childId, { lastPumpVolume: session.volumeMl });
  }
  if (session.type === 'bottle' && session.volumeMl) {
    await updateChild(childId, { lastBottleVolume: session.volumeMl });
  }

  return entry;
}

export async function getRecentSessions(childId: string, limit: number = 50): Promise<Session[]> {
  const sessions = await db.sessions
    .where('childId')
    .equals(childId)
    .reverse()
    .sortBy('startTime');
  return sessions.filter((s) => !s.deletedAt).slice(0, limit);
}

export async function getSessionsByDay(childId: string, date: number): Promise<Session[]> {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const sessions = await db.sessions
    .where('[childId+startTime]')
    .between([childId, dayStart.getTime()], [childId, dayEnd.getTime()])
    .toArray();

  return sessions.filter((s) => !s.deletedAt);
}

export async function getLastSession(childId: string): Promise<Session | undefined> {
  const sessions = await getRecentSessions(childId, 1);
  return sessions[0];
}

export async function updateSession(id: string, updates: Partial<Session>): Promise<void> {
  await db.sessions.update(id, { ...updates, updatedAt: Date.now() });
}

export async function deleteSession(id: string): Promise<void> {
  await db.sessions.update(id, { deletedAt: Date.now(), updatedAt: Date.now() });
}

export function getSuggestedSide(childId: string): 'left' | 'right' {
  const last = getChildById(childId)?.lastBreastUsed;
  if (last === 'left') return 'right';
  if (last === 'right') return 'left';
  return 'left';
}
